import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { addDoc, collection, deleteDoc, doc, getDocs, getFirestore, query, updateDoc, where } from 'firebase/firestore';
import { MAPBOX_ACCESS_TOKEN } from '../../config/mapbox-config';
import { FONT } from '../../styles/tourTheme';
import { fieldWellStyle, webInputClassProps, V2_INPUT_WELL_CLASS } from '../../utils/transparentTextInputStyle';
import {
  annotateSavedContacts,
  stopsFromTour,
  visibleSavedContacts,
} from '../../utils/savedContacts';

async function geocodeAddress(address) {
  const queryText = String(address || '').trim();
  if (!queryText || !MAPBOX_ACCESS_TOKEN) return null;
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(queryText)}.json`
    + `?access_token=${MAPBOX_ACCESS_TOKEN}&limit=1&types=address,poi,place,locality`;
  const res = await fetch(url);
  if (!res.ok) return null;
  const data = await res.json();
  const feature = data?.features?.[0];
  const center = feature?.center;
  if (!Array.isArray(center) || center.length < 2) return null;
  return {
    longitude: Number(center[0]),
    latitude: Number(center[1]),
    address: String(feature.place_name || queryText),
  };
}

function wellStyle(pal, isDarkMode) {
  return [
    fieldWellStyle(pal, isDarkMode),
    webInputClassProps(V2_INPUT_WELL_CLASS),
  ];
}

export function SavedContactsCard({
  pal,
  isDarkMode = false,
  userId,
  userName,
  tour,
  dateKey,
  canEdit = true,
  previewLimit = 0,
}) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [queryText, setQueryText] = useState('');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const stops = useMemo(() => stopsFromTour(tour), [tour]);

  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setContacts([]);
      return undefined;
    }
    setLoading(true);
    (async () => {
      try {
        const db = getFirestore();
        const snap = await getDocs(query(collection(db, 'userLocations'), where('userId', '==', userId)));
        if (cancelled) return;
        setContacts(snap.docs.map((row) => ({ id: row.id, ...row.data(), isOwn: true })));
        setError('');
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Could not load saved contacts.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const ranked = useMemo(() => {
    const rows = visibleSavedContacts(contacts, { query: queryText, stops, dateKey, limit: 0 });
    return annotateSavedContacts(rows, stops);
  }, [contacts, queryText, stops, dateKey]);
  const searching = Boolean(String(queryText || '').trim());
  const visible = searching || !previewLimit ? ranked : ranked.slice(0, previewLimit);

  const saveNew = async () => {
    const nextName = String(name || '').trim();
    const nextAddress = String(address || '').trim();
    if (!userId) {
      setError('Sign in to save a contact.');
      return;
    }
    if (!nextName || !nextAddress) {
      setError('Name and address are both required.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const geo = await geocodeAddress(nextAddress);
      if (!geo) {
        setError('That address did not resolve. Try a street, city, or place name.');
        return;
      }
      const db = getFirestore();
      const locationData = {
        userId,
        userName: userName || 'You',
        name: nextName,
        notes: '',
        category: 'contact',
        address: geo.address,
        latitude: geo.latitude,
        longitude: geo.longitude,
        isPublic: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      const docRef = await addDoc(collection(db, 'userLocations'), locationData);
      setContacts((prev) => [...prev, { id: docRef.id, ...locationData, isOwn: true }]);
      setName('');
      setAddress('');
    } catch (err) {
      setError(err?.message || 'Could not save that contact.');
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (contact) => {
    const nextName = String(editName || '').trim();
    const nextAddress = String(editAddress || '').trim();
    if (!nextName || !nextAddress) {
      setError('Name and address are both required.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const addressChanged = nextAddress !== String(contact.address || '').trim();
      const geo = addressChanged ? await geocodeAddress(nextAddress) : null;
      if (addressChanged && !geo) {
        setError('That address did not resolve. Try a street, city, or place name.');
        return;
      }
      const updates = {
        name: nextName,
        address: geo?.address || nextAddress,
        ...(geo ? { latitude: geo.latitude, longitude: geo.longitude } : {}),
        updatedAt: new Date().toISOString(),
      };
      const db = getFirestore();
      await updateDoc(doc(db, 'userLocations', contact.id), updates);
      setContacts((prev) => prev.map((row) => (row.id === contact.id ? { ...row, ...updates } : row)));
      setEditingId(null);
    } catch (err) {
      setError(err?.message || 'Could not update that contact.');
    } finally {
      setBusy(false);
    }
  };

  const removeContact = async (contact) => {
    setBusy(true);
    setError('');
    try {
      const db = getFirestore();
      await deleteDoc(doc(db, 'userLocations', contact.id));
      setContacts((prev) => prev.filter((row) => row.id !== contact.id));
      if (editingId === contact.id) setEditingId(null);
    } catch (err) {
      setError(err?.message || 'Could not delete that contact.');
    } finally {
      setBusy(false);
    }
  };

  const inputStyle = {
    color: pal.ink,
    fontSize: 15,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    minWidth: 140,
  };

  return (
    <View style={{
      marginTop: 18,
      width: '100%',
      borderWidth: 1,
      borderColor: pal.line,
      borderRadius: 12,
      backgroundColor: pal.surf,
      padding: 18,
    }}
    >
      <Text style={{ fontFamily: FONT.mono, fontSize: 12, letterSpacing: 1.2, textTransform: 'uppercase', color: pal.mute, marginBottom: 6 }}>
        Saved contacts
      </Text>
      <Text style={{ color: pal.ink, fontSize: 14, fontWeight: '600', marginBottom: 8 }}>
        Only visible to you.
      </Text>
      <Text style={{ color: pal.ink, fontSize: 16, fontWeight: '600', marginBottom: 12 }}>
        {searching
          ? 'Matches for this search.'
          : `Nearest to this show. Search to look through every saved contact.`}
      </Text>
      <TextInput
        value={queryText}
        onChangeText={setQueryText}
        placeholder="Search saved contacts"
        placeholderTextColor={pal.faint}
        accessibilityLabel="Search saved contacts"
        {...wellStyle(pal, isDarkMode)[1]}
        style={[wellStyle(pal, isDarkMode)[0], inputStyle, { width: '100%', marginBottom: 12 }]}
      />
      {canEdit ? (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
          <TextInput
            value={name}
            onChangeText={setName}
            placeholder="Name"
            placeholderTextColor={pal.faint}
            accessibilityLabel="New contact name"
            {...wellStyle(pal, isDarkMode)[1]}
            style={[wellStyle(pal, isDarkMode)[0], inputStyle, { flexGrow: 1, flexBasis: 160 }]}
          />
          <TextInput
            value={address}
            onChangeText={setAddress}
            placeholder="Address"
            placeholderTextColor={pal.faint}
            accessibilityLabel="New contact address"
            {...wellStyle(pal, isDarkMode)[1]}
            style={[wellStyle(pal, isDarkMode)[0], inputStyle, { flexGrow: 2, flexBasis: 220 }]}
          />
          <TouchableOpacity
            onPress={saveNew}
            disabled={busy}
            accessibilityLabel="Save contact"
            style={{
              backgroundColor: pal.ink,
              borderRadius: 8,
              paddingHorizontal: 16,
              paddingVertical: 10,
              justifyContent: 'center',
              opacity: busy ? 0.6 : 1,
            }}
          >
            <Text style={{ color: pal.page, fontWeight: '600' }}>{busy ? 'Saving…' : 'Save contact'}</Text>
          </TouchableOpacity>
        </View>
      ) : null}
      {error ? <Text style={{ color: pal.warn || '#c0483c', marginBottom: 8 }}>{error}</Text> : null}
      {loading ? <ActivityIndicator color={pal.ink} /> : null}
      {!loading && !searching && previewLimit > 0 && ranked.length > visible.length ? (
        <Text style={{ color: pal.mute, fontSize: 13, marginBottom: 8 }}>
          {`Showing the ${visible.length} nearest. Search to see the rest.`}
        </Text>
      ) : null}
      {!loading && !visible.length ? (
        <Text style={{ color: pal.mute }}>
          {queryText
            ? 'No saved contacts match that search.'
            : 'No saved contacts near this show. Add one above, or search to see the rest.'}
        </Text>
      ) : null}
      {visible.map((contact) => {
        const editing = editingId === contact.id;
        const miles = contact.nearest ? Math.round(contact.nearest.miles) : null;
        const nearLabel = contact.nearest
          ? `${miles} mi from ${contact.nearest.stop.label}`
          : 'Not near a mapped stop';
        return (
          <View
            key={contact.id}
            style={{
              borderTopWidth: 1,
              borderTopColor: pal.lineSoft || pal.line,
              paddingVertical: 12,
              gap: 8,
            }}
          >
            {editing ? (
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
                <TextInput
                  value={editName}
                  onChangeText={setEditName}
                  placeholder="Name"
                  placeholderTextColor={pal.faint}
                  {...wellStyle(pal, isDarkMode)[1]}
                  style={[wellStyle(pal, isDarkMode)[0], inputStyle, { flexGrow: 1, flexBasis: 160 }]}
                />
                <TextInput
                  value={editAddress}
                  onChangeText={setEditAddress}
                  placeholder="Address"
                  placeholderTextColor={pal.faint}
                  {...wellStyle(pal, isDarkMode)[1]}
                  style={[wellStyle(pal, isDarkMode)[0], inputStyle, { flexGrow: 2, flexBasis: 220 }]}
                />
                <TouchableOpacity onPress={() => saveEdit(contact)} disabled={busy}>
                  <Text style={{ color: pal.teal, fontWeight: '700', paddingVertical: 8 }}>Save</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={() => setEditingId(null)}>
                  <Text style={{ color: pal.mute, fontWeight: '600', paddingVertical: 8 }}>Cancel</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <View style={{ flex: 1, minWidth: 180 }}>
                  <Text style={{ color: pal.ink, fontSize: 16, fontWeight: '700' }}>{contact.name || 'Contact'}</Text>
                  <Text style={{ color: pal.mute, marginTop: 2 }}>{contact.address || 'No address'}</Text>
                  <Text style={{ color: pal.ink, marginTop: 4, fontSize: 13 }}>{nearLabel}</Text>
                </View>
                {canEdit ? (
                  <View style={{ flexDirection: 'row', gap: 14, alignItems: 'center' }}>
                    <TouchableOpacity
                      onPress={() => {
                        setEditingId(contact.id);
                        setEditName(contact.name || '');
                        setEditAddress(contact.address || '');
                      }}
                      accessibilityLabel={`Edit ${contact.name || 'contact'}`}
                    >
                      <Text style={{ color: pal.ink, fontWeight: '700' }}>Edit</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() => removeContact(contact)}
                      accessibilityLabel={`Delete ${contact.name || 'contact'}`}
                    >
                      <Text style={{ color: pal.warn || '#c0483c', fontWeight: '700' }}>Delete</Text>
                    </TouchableOpacity>
                  </View>
                ) : null}
              </View>
            )}
          </View>
        );
      })}
    </View>
  );
}
